/**
 * Shared utility functions for the Station Assignment feature.
 */

/**
 * Format a full name as "First L." for compact display.
 * Two or more tokens: "First L." — single token or empty: unchanged.
 */
export function formatDisplayName(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(t => t.length > 0);
  if (tokens.length >= 2) {
    return `${tokens[0]} ${tokens[tokens.length - 1][0]}.`;
  }
  return name;
}

export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
};

/** Base URL of the internal badge photo CDN. */
const BADGE_PHOTO_BASE_URL = 'https://cdn.prod.badgephotos.side.amazon.dev/';

/**
 * Build the badge photo URL for an associate from their login alias.
 * Returns `undefined` when no alias is available, so callers can fall back
 * to rendering initials.
 */
export const getBadgePhotoUrl = (alias: string | undefined | null): string | undefined => {
  const trimmed = alias?.trim();
  if (!trimmed) {
    return undefined;
  }
  return `${BADGE_PHOTO_BASE_URL}?login=${encodeURIComponent(trimmed)}`;
};

/**
 * The label shown for an associate in the Station Assignment UI: the login
 * alias when present, falling back to the full name when the alias is missing
 * or blank. Kept in one place so every surface (cards, line slots, typeahead
 * options, modal headers) displays associates consistently.
 */
export const getAssociateDisplayLabel = (
  alias: string | undefined | null,
  name: string,
): string => {
  const trimmed = alias?.trim();
  return trimmed || name;
};

/**
 * Summarise an associate's trained-paths list for compact display. Returns
 * `null` when there is nothing to show (missing or empty list) so callers can
 * render nothing. Otherwise returns a short `summary` (up to `maxVisible`
 * paths, with a `+N more` suffix when truncated), a `full` comma-joined list
 * (for a tooltip / "see full list"), and a `truncated` flag.
 *
 * Defensive against an `undefined` list, since the facade may omit
 * `trainedPaths` entirely.
 */
export const getTrainedPathsSummary = (
  trainedPaths: string[] | undefined | null,
  maxVisible = 3,
): { summary: string; full: string; truncated: boolean } | null => {
  const paths = (trainedPaths ?? []).filter(p => p && p.trim().length > 0);
  if (paths.length === 0) {
    return null;
  }
  const full = paths.join(', ');
  if (paths.length <= maxVisible) {
    return { summary: full, full, truncated: false };
  }
  const summary = `${paths.slice(0, maxVisible).join(', ')} +${paths.length - maxVisible} more`;
  return { summary, full, truncated: true };
};